import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import { fetchFactoriesForManagement, deleteFactory } from '../../services/api';
import { hasPermission } from '../../services/authService';
import './FactoryListPage.css';

function FactoryListPage() {
    const navigate = useNavigate();
    const [selectedStatus, setSelectedStatus] = useState('전체');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedFactory, setSelectedFactory] = useState(null);
    const [factories, setFactories] = useState([]);
    const [loading, setLoading] = useState(true);

    const [allFactories, setAllFactories] = useState([]); // 전체 공장 데이터

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    // 권한 체크
    const canCreate = hasPermission('factory.create');
    const canUpdate = hasPermission('factory.update');
    const canDelete = hasPermission('factory.delete');

    // API에서 공장 목록 가져오기
    useEffect(() => {
        const loadFactories = async () => {
            try {
                setLoading(true);
                const data = await fetchFactoriesForManagement();
                

                // API 데이터를 화면에 맞게 변환
                const factoriesData = Array.isArray(data) ? data : [];
                const transformedFactories = factoriesData.map(factory => ({
                    id: factory.id,
                    name: factory.factoryName,
                    code: factory.factoryCode,
                    location: factory.address,
                    temperature: factory.temperature ? `${factory.temperature}°C` : '-',
                    humidity: factory.humidity ? `${factory.humidity}%` : '-',
                    equipmentStatus: {
                        total: factory.totalEquipment || 0,
                        running: factory.runningEquipment || 0,
                        idle: factory.idleEquipment || 0,
                        stopped: factory.stoppedEquipment || 0
                    },
                    operationRate: factory.operationRate || 0,
                    status: factory.status === 'NORMAL' ? '정상' : factory.status === 'WARNING' ? '경고' : '이상',
                    lastInspection: factory.lastInspectionDate || '-',
                    manager: factory.managerName || '-'
                }));

                setAllFactories(transformedFactories);
                setFactories(transformedFactories);
            } catch (err) {
                console.error('공장 목록 조회 실패:', err);
                setAllFactories([]);
                setFactories([]);
            } finally {
                setLoading(false);
            }
        };

        loadFactories();
    }, []);

    // 필터링 로직
    useEffect(() => {
        let filtered = [...allFactories];

        // 상태 필터링
        if (selectedStatus !== '전체') {
            filtered = filtered.filter(factory => factory.status === selectedStatus);
        }

        // 검색어 필터링
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(factory =>
                factory.name.toLowerCase().includes(query) ||
                factory.code.toLowerCase().includes(query)
            );
        }

        setFactories(filtered);
        setPage(0); // 필터 변경 시 첫 페이지로
    }, [selectedStatus, searchQuery, allFactories]);

    // 페이지네이션 적용
    const paginatedFactories = useMemo(() => {
        const startIndex = page * size;
        return factories.slice(startIndex, startIndex + size);
    }, [factories, page, size]);

    const totalElements = factories.length;
    const totalPages = Math.ceil(totalElements / size);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const getStatusClass = (status) => {
        switch (status) {
            case '정상':
                return 'status-normal';
            case '경고':
                return 'status-warning';
            case '이상':
                return 'status-error';
            default:
                return 'status-normal';
        }
    };

    const getOperationRateClass = (rate) => {
        if (rate >= 85) return 'rate-high';
        if (rate >= 70) return 'rate-medium';
        return 'rate-low';
    };

    const handleFactoryClick = (factoryId) => {
        navigate(`/factory/${factoryId}`);
    };

    const handleEditClick = (factoryId) => {
        navigate(`/factory/${factoryId}?edit=true`);
    };

    const handleAddFactory = () => {
        navigate('/factory/register');
    };

    const handleDeleteClick = (factory) => {
        setSelectedFactory(factory);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            
            await deleteFactory(selectedFactory.id);

            alert(`"${selectedFactory.name}" 공장이 삭제되었습니다.`);
            setIsDeleteModalOpen(false);

            // 목록 새로고침
            const data = await fetchFactoriesForManagement();
            const factoriesData = Array.isArray(data) ? data : [];
            const transformedFactories = factoriesData.map(factory => ({
                id: factory.id,
                name: factory.factoryName,
                code: factory.factoryCode,
                location: factory.address,
                temperature: factory.temperature ? `${factory.temperature}°C` : '-',
                humidity: factory.humidity ? `${factory.humidity}%` : '-',
                equipmentStatus: {
                    total: factory.totalEquipment || 0,
                    running: factory.runningEquipment || 0,
                    idle: factory.idleEquipment || 0,
                    stopped: factory.stoppedEquipment || 0
                },
                operationRate: factory.operationRate || 0,
                status: factory.status === 'NORMAL' ? '정상' : factory.status === 'WARNING' ? '경고' : '이상',
                lastInspection: factory.lastInspectionDate || '-',
                manager: factory.managerName || '-'
            }));

            setAllFactories(transformedFactories);
            setFactories(transformedFactories);
        } catch (error) {
            console.error('공장 삭제 실패:', error);
            alert('공장 삭제에 실패했습니다.');
        }
    };

    return (
        <MainLayout>
            <div className="factory-list-container">
                {/* Header Section */}
                <div className="factory-list-header">
                    <div className="factory-list-header-left">
                        <div className="factory-list-title">공장 관리</div>
                        <p className="factory-list-subtitle">공장 현황 관리</p>
                    </div>
                    <div className="factory-list-header-right">
                        {/* 추가 정보 표시 가능 */}
                    </div>
                </div>

                <div className="factory-list-content">
                    {/* Filter and Search Section */}
                    <div className="factory-list-controls">
                        <div className="control-left">
                            <label className="control-label">상태</label>
                            <div className="dropdown-container">
                                <select
                                    className="status-dropdown"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    <option value="전체">전체</option>
                                    <option value="정상">정상</option>
                                    <option value="경고">경고</option>
                                    <option value="이상">이상</option>
                                </select>
                                <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div className="control-right">
                            <div className="search-container">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="공장명 또는 코드 검색"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            // 엔터 키 입력 시 이미 onChange로 필터링되고 있으므로 추가 동작 불필요
                                            e.preventDefault();
                                        }
                                    }}
                                />
                                <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            {canCreate && (
                                <button className="add-factory-btn" onClick={handleAddFactory}>
                                    <svg className="plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    공장 추가
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Factory Table */}
                    <div className="factory-table-container">
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                                데이터를 불러오는 중...
                            </div>
                        ) : (
                            <>
                            <table className="factory-table">
                                <thead>
                                    <tr>
                                        <th>공장명</th>
                                        <th>코드</th>
                                        <th>위치</th>
                                        <th>온도</th>
                                        <th>습도</th>
                                        <th>설비현황</th>
                                        <th>가동률</th>
                                        <th>상태</th>
                                        <th>최종점검일</th>
                                        <th>작업</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedFactories.map((factory) => (
                                    <tr key={factory.id} onClick={() => handleFactoryClick(factory.id)} style={{ cursor: 'pointer' }}>
                                        <td className="factory-name-cell">
                                            <div className="factory-name-info">
                                                <div className="factory-icon">
                                                    <span className="material-symbols-outlined">factory</span>
                                                </div>
                                                <div className="factory-details">
                                                    <div className="factory-name">{factory.name}</div>
                                                    <div className="factory-manager">담당: {factory.manager}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="factory-code">{factory.code}</span>
                                        </td>
                                        <td>{factory.location}</td>
                                        <td>
                                            <div className="environment-value">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '4px' }}>
                                                    <path d="M7 2V8M7 8C6.46957 8 5.96086 8.21071 5.58579 8.58579C5.21071 8.96086 5 9.46957 5 10C5 10.5304 5.21071 11.0391 5.58579 11.4142C5.96086 11.7893 6.46957 12 7 12C7.53043 12 8.03914 11.7893 8.41421 11.4142C8.78929 11.0391 9 10.5304 9 10C9 9.46957 8.78929 8.96086 8.41421 8.58579C8.03914 8.21071 7.53043 8 7 8Z" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {factory.temperature}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="environment-value">
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: '4px' }}>
                                                    <path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M7 5V9" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {factory.humidity}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="equipment-status">
                                                <span className="equipment-total">총 {factory.equipmentStatus.total}대</span>
                                                <div className="equipment-breakdown">
                                                    <span className="equipment-running">가동 {factory.equipmentStatus.running}</span>
                                                    <span className="equipment-idle">대기 {factory.equipmentStatus.idle}</span>
                                                    <span className="equipment-stopped">정지 {factory.equipmentStatus.stopped}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="operation-rate">
                                                <div className="rate-bar-container">
                                                    <div
                                                        className={`rate-bar ${getOperationRateClass(factory.operationRate)}`}
                                                        style={{ width: `${factory.operationRate}%` }}
                                                    ></div>
                                                </div>
                                                <span className="rate-text">{factory.operationRate}%</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStatusClass(factory.status)}`}>
                                                {factory.status}
                                            </span>
                                        </td>
                                        <td>{factory.lastInspection}</td>
                                        <td>
                                            <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                                                {canUpdate && (
                                                    <button className="action-btn edit-btn" onClick={() => handleEditClick(factory.id)}>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M11.3333 2C11.5084 1.82493 11.7163 1.68605 11.9457 1.59135C12.1752 1.49665 12.4218 1.44803 12.6707 1.44803C12.9196 1.44803 13.1662 1.49665 13.3956 1.59135C13.6251 1.68605 13.833 1.82493 14.008 2C14.183 2.17507 14.3219 2.38299 14.4166 2.61245C14.5113 2.84191 14.5599 3.08852 14.5599 3.33744C14.5599 3.58636 14.5113 3.83297 14.4166 4.06243C14.3219 4.29189 14.183 4.49981 14.008 4.67488L5.33333 13.3495L1.33333 14.683L2.66667 10.6829L11.3333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button className="action-btn delete-btn" onClick={() => handleDeleteClick(factory)}>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M2 4H3.33333H14M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4H12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    ))}
                                </tbody>
                            </table>
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                totalElements={totalElements}
                                size={size}
                                onPageChange={handlePageChange}
                                onSizeChange={handleSizeChange}
                            />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="공장 삭제"
                message={selectedFactory ? `"${selectedFactory.name}" 공장을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.` : ''}
                confirmText="삭제"
                cancelText="취소"
                type="danger"
            />
        </MainLayout>
    );
}

export default FactoryListPage;
